import React from "react";
import { Button } from "../../components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6992dc80a_WeSonder1024x1024.png";

export default function HeroSection() {
  const createPageUrl = (pageName: string) => {
    // Map page names to their correct URL paths
    const pagePaths: { [key: string]: string } = {
      'Downloads': '/downloads',
      'ForMyClients': '/for-my-clients',
      'Home': '/home'
    };
    
    return pagePaths[pageName] || `/${pageName.toLowerCase()}`;
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="text-center">
          <h1 className="hero-headline text-slate-900 mb-8 max-w-5xl mx-auto">
            Understand Your <span className="text-blue-500">Personality</span> Through Your Messages
          </h1>
          
          <p className="hero-subheadline text-slate-600 mb-12 max-w-4xl mx-auto">
            WeSonder provides a deep, personalized analysis of your primary relationship, drawing objective insights from your actual, unfiltered text messages using advanced AI.
          </p>
          
          <div className="flex justify-center mb-16">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={createPageUrl("Downloads")}>
                <Button 
                  size="lg" 
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 text-lg font-semibold group transition-all duration-200"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Get the App
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Available Now</span>
              </div>
            </div>
          </div>
          
          {/* Hero Visual */}
          <div className="relative max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
              <div className="bg-slate-100 px-6 py-4 border-b border-slate-200">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div className="flex-1 text-center">
                    <span className="text-sm font-medium text-slate-600">WeSonder Analysis</span>
                  </div>
                </div>
              </div>
              
              <div className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">Your Communication Style</h3>
                      <p className="text-sm opacity-90">You tend to be direct and solution-oriented, with a preference for clarity over ambiguity.</p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">Attachment Pattern</h3>
                      <p className="text-sm opacity-90">Shows secure attachment with occasional anxious tendencies during conflicts.</p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">Growth Opportunity</h3>
                      <p className="text-sm opacity-90">Consider using more emotional validation before offering solutions.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <img src={LOGO_URL} alt="WeSonder Logo" className="w-32 h-32"/>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">AI</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* WaitlistModal component was removed as per the edit hint */}
    </section>
  );
} 